import { JWTPayload, SignJWT, decodeJwt } from 'jose';
import { useCallback, useEffect, useState } from 'react';

import { tryCatch } from '@helper/functions/utility';
import useQueryParameters from '@helper/hooks/useQueryParameters';
import { paytoPostNotification } from '@helper/service/payment';
import { useMockPayToStore } from '@store/mock-payto-banking';

export default function MockPaytoBanking() {
  const query = useQueryParameters();
  const setRejected = useMockPayToStore((s) => s.setRejected);
  const isCancelled = useMockPayToStore((s) => s.isCancelled);
  const setCancelled = useMockPayToStore((s) => s.setCancelled);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [state, setState] = useState('init');
  const [payload, setPayload] = useState<{
    mps_user_id: string;
    mandate_id: string;
    action_id: string;
    merchant: string;
    amount: string;
    type: string;
    description: string;
    healthEntityCode: string;
  }>(null);

  const handlePayToApproval = useCallback(async () => {
    if (!payload) return;
    setLoading(true);
    const healthEntityCode = payload.healthEntityCode || 'DEFAULT';
    const [err] = await tryCatch(
      paytoPostNotification({
        mps_user_id: payload.mps_user_id,
        mandate_identification: payload.mandate_id,
        action_identification: payload.action_id,
        trigger: 'MCRC',
        mandate_status: 'ACTV',
        action_type: 'CREA',
        action_status: 'CMPL',
      }, healthEntityCode),
    );

    if (err) {
      console.error(err);
      setError('Error submitting request');
    } else {
      setState('completed');
    }

    setLoading(false);
  }, [payload]);

  const handlePayToReject = useCallback(async () => {
    if (!payload) return;
    setLoading(true);
    const healthEntityCode = payload.healthEntityCode || 'DEFAULT';
    // const [err] = await tryCatch(
    //   paytoPostNotification({
    //     mps_user_id: payload.mps_user_id,
    //     mandate_identification: payload.mandate_id,
    //     action_identification: payload.action_id,
    //     trigger: 'MAMD', 
    //     mandate_status: 'ACTV', 
    //     action_type: 'CREA',
    //     action_status: 'DECL', 
    //   }, healthEntityCode),
    // );
    setRejected(true);
    localStorage.setItem('payto-rejected', 'true');
    setState('completed');
    setLoading(false);
  }, [payload, setRejected]);

  useEffect(() => {
    const token = query.get('token');
    if (!token) return;

    try {
      const data = decodeJwt(token) as JWTPayload & {
        mps_user_id: string;
        mandate_id: string;
        action_id: string;
        merchant: string;
        amount: string;
        type: string;
        description: string;
        healthEntityCode: string;
      };

      setPayload(data);
      setState('idle');
    } catch (err) {
      console.error(err);
      setError('Error decoding request details');
    }
  }, [query]);

  // Listen for localStorage changes across tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'payto-cancelled' && e.newValue === 'true') {
        setCancelled(true);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also check initial state
    if (localStorage.getItem('payto-cancelled') === 'true') {
      setCancelled(true);
    }

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [setCancelled]);

  useEffect(() => {
    new SignJWT({
      mps_user_id: 'mps',
      mandate_id: crypto.randomUUID(),
      action_id: crypto.randomUUID(),
      merchant: 'Health NSW',
      amount: '25.00',
      type: 'One-Off Payment',
      description: 'One-Off Payment',
    })
      .setProtectedHeader({ alg: 'HS256' })
      .setIssuedAt()
      .setExpirationTime('15m')
      // just a random placeholder key
      .sign(crypto.getRandomValues(new Uint8Array(32)))
      .then((token) => {
        console.log(token);
      });
  }, []);

  return (
    <>
      <div className="tw-font-sans tw-w-full tw-h-screen tw-max-w-screen tw-max-h-screen tw-overflow-auto tw-flex tw-items-center tw-justify-center tw-bg-gray-50">
        <div className="tw-p-6 tw-border tw-border-gray-700 tw-rounded-xl tw-flex tw-flex-col tw-justify-start tw-gap-4 tw-bg-white">
          <h1 className="tw-text-xl">Payment Mandate Request</h1>

          {(payload && state === 'idle' && !isCancelled) && (
            <>
              <ul className="tw-p-2">
                <li className="tw-flex tw-items-center tw-gap-4">
                  <span>Merchant:</span>
                  <span>{payload?.merchant}</span>
                </li>
                <li className="tw-flex tw-items-center tw-gap-4">
                  <span>Amount:</span>
                  <span>{payload?.amount}</span>
                </li>
                <li className="tw-flex tw-items-center tw-gap-4">
                  <span>Type:</span>
                  <span>{payload?.type}</span>
                </li>
                <li className="tw-flex tw-items-center tw-gap-4">
                  <span>Description:</span>
                  <span>{payload?.description}</span>
                </li>
              </ul>
              <div className="tw-grid tw-grid-cols-2 tw-gap-6">
                <button
                  className="tw-border-none tw-rounded tw-p-2 tw-bg-red-100 hover:tw-cursor-pointer tw-w-full tw-text-base"
                  disabled={loading}
                  onClick={handlePayToReject}
                >
                  Reject
                </button>
                <button
                  className="tw-border-none tw-rounded tw-p-2 tw-bg-green-100 hover:tw-cursor-pointer tw-w-full tw-text-base"
                  disabled={loading}
                  onClick={handlePayToApproval}
                >
                  Approve
                </button>
              </div>
            </>
          )}

          {state === 'completed' && (
            <div>
              <span>Action completed. You may now close this window.</span>
            </div>
          )}

          {isCancelled && (
            <div>
              <span>Action aborted. User has cancelled the transaction. You may now close this window.</span>
            </div>
          )}

          {error && (
            <div>
              <span>{error}</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
